import { useEffect, useRef } from 'react';
import {
  accessFromStatus,
  cachedDeviceAccess,
  rememberDeviceAccess,
  type DeviceAccess,
} from './deviceAccess';

interface Registration {
  data?: { registered: boolean; required?: boolean };
  /** True once the question has been asked and answered, or has failed. */
  isFetched: boolean;
}

interface Boot {
  userId: string;
  /** What was remembered for this person before this boot. */
  cached: DeviceAccess | null;
  /** The access the app started in. A different answer later means starting over. */
  started: DeviceAccess | null;
}

/**
 * Settles how the Android app runs before anything that depends on it starts.
 *
 * The data mode (offline database or straight to the server) is chosen when the
 * till first mounts, so it has to be known by then:
 *
 *  - With an answer remembered for this person, use it at once. A registered
 *    tablet switched on with no signal still comes up in its offline mode.
 *  - Without one - the first time this person signs in on this device - wait
 *    for the server. They have just signed in, so it is reachable.
 *  - If the server cannot be asked, carry on as a terminal, which is how the
 *    app has always started.
 *
 * If the answer later differs from the one the app started with (the owner
 * paired the device, or turned the requirement on), the page reloads once so it
 * starts again in the right mode. Reloading beats switching data source under a
 * till that is open.
 */
export function useKioskAccess(
  active: boolean,
  userId: string | undefined,
  registration: Registration,
): { ready: boolean } {
  const boot = useRef<Boot | null>(null);
  if (active && userId && boot.current?.userId !== userId) {
    boot.current = { userId, cached: cachedDeviceAccess(userId), started: null };
  }
  const state = boot.current;

  const answered = registration.data ? accessFromStatus(registration.data) : null;
  const ready = !active || !userId || state?.cached != null || registration.isFetched;

  if (active && state && ready && state.started === null) {
    state.started = state.cached ?? answered ?? 'terminal';
  }
  if (active && userId && answered) rememberDeviceAccess(userId, answered);

  const started = state?.started ?? null;
  useEffect(() => {
    if (active && started && answered && answered !== started) window.location.reload();
  }, [active, started, answered]);

  return { ready };
}
